import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFarm } from '../../contexts/FarmContext';
import { farmAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import FlockTimingFields from '../../components/farm/FlockTimingFields';
import KenyaLocationFields from '../../components/common/KenyaLocationFields';
import { formatPlace } from '../../data/kenyaLocations';
import {
  apiDateToInput,
  biologicalAgeDays,
  dateInputToApi,
  formatAge,
  MAX_INITIAL_AGE_DAYS,
  parseAgeDays,
  todayInputValue
} from '../../utils/flockAge';
import {
  BuildingOfficeIcon,
  InformationCircleIcon,
  PlusIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';

const TYPE_CARDS = [
  { id: 'broiler', title: 'Broilers', hint: 'Meat birds, usually 6–8 weeks to market' },
  { id: 'layer', title: 'Layers', hint: 'Egg birds, laying from about 18 weeks' },
  { id: 'dual', title: 'Kienyeji', hint: 'Dual-purpose, improved or indigenous' }
];

const chickenBreeds = {
  layer: ['Hy-Line Brown', 'Hy-Line White', 'Lohmann Brown', 'Lohmann White', 'ISA Brown', 'Dekalb White', 'Rhode Island Red', 'White Leghorn', 'Black Star', 'Red Star'],
  broiler: ['Cobb 500', 'Ross 308', 'Ross 708', 'Arbor Acres', 'Hubbard', 'Marshall', 'Indian River', 'Cornish Cross', 'Red Ranger', 'Freedom Ranger']
};
const kienyejiImprovedBreeds = ['Kenbro', 'Kuroiler', 'Sasso', 'Rainbow Rooster'];
const kienyejiPureBreeds = ['Indigenous (Kienyeji)'];
const breedOptions = {
  layer: chickenBreeds.layer,
  broiler: chickenBreeds.broiler,
  dual: ['Rhode Island Red', 'Sussex', 'Plymouth Rock', 'New Hampshire', 'Australorp']
};
const housingOptions = [
  { value: 'deep_litter', label: 'Deep litter' },
  { value: 'free_range', label: 'Free range' },
  { value: 'barn', label: 'Barn' },
  { value: 'pasture', label: 'Pasture' },
  { value: 'cage', label: 'Cage' },
  { value: 'other', label: 'Other' }
];
const feedingOptions = [
  { value: 'manual', label: 'Manual' },
  { value: 'trough', label: 'Trough' },
  { value: 'automatic', label: 'Automatic' },
  { value: 'chain_feeder', label: 'Chain feeder' },
  { value: 'other', label: 'Other' }
];

const pretty = (value, list) => (list.find((x) => x.value === value)?.label) || (value || '').replace(/_/g, ' ');

const FarmSetup = () => {
  const { createFarm, getFarm, updateFarm } = useFarm();
  const [searchParams] = useSearchParams();
  const editId = searchParams.get('edit');
  const isEdit = Boolean(editId);
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [customBreed, setCustomBreed] = useState(false);
  const [addFlockNow, setAddFlockNow] = useState(true);
  const [showMore, setShowMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [extraCohorts, setExtraCohorts] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    location: '',
    county: '',
    subCounty: '',
    town: '',
    coordinates: null,
    setupData: {
      chickenCount: '',
      chickenType: 'broiler',
      chickAge: '',
      setupDate: todayInputValue(),
      breed: '',
      housingType: '',
      feedingSystem: '',
      managementSystem: '',
      subtype: '',
      targetMarketAgeDays: ''
    },
    notes: ''
  });

  const steps = isEdit
    ? [{ id: 1, name: 'Farm', description: 'Name and location' }]
    : [
        { id: 1, name: 'Farm', description: 'Name and location' },
        { id: 2, name: 'Flock', description: 'Birds you have now' },
        { id: 3, name: 'Review', description: 'Check and create' }
      ];

  React.useEffect(() => {
    const load = async () => {
      if (!isEdit) return;
      const res = await getFarm(editId);
      if (res?.success && res.farm) {
        const f = res.farm;
        setFormData({
          name: f.name || '',
          location: f.location || '',
          county: f.county || '',
          subCounty: f.subCounty || '',
          town: f.town || '',
          coordinates: f.coordinates || null,
          setupData: {
            chickenCount: f.setupData?.chickenCount ?? '',
            chickenType: f.setupData?.chickenType || 'broiler',
            chickAge: f.setupData?.chickAge ?? '',
            setupDate: apiDateToInput(f.setupData?.setupDate) || todayInputValue(),
            breed: f.setupData?.breed || '',
            housingType: f.setupData?.housingType || '',
            feedingSystem: f.setupData?.feedingSystem || '',
            managementSystem: f.setupData?.managementSystem || '',
            subtype: f.setupData?.subtype || '',
            targetMarketAgeDays: f.setupData?.targetMarketAgeDays ?? ''
          },
          notes: f.notes || ''
        });
      }
    };
    load();
  }, [isEdit, editId]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.startsWith('setupData.')) {
      const field = name.split('.')[1];
      let processedValue = value;
      if (field === 'chickenCount' || field === 'chickAge' || field === 'targetMarketAgeDays') {
        processedValue = value === '' ? '' : Number(value);
      } else if (field === 'setupDate') {
        processedValue = apiDateToInput(value);
      }
      setFormData((prev) => ({
        ...prev,
        setupData: { ...prev.setupData, [field]: processedValue }
      }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  const setType = (id) => {
    setFormData((prev) => ({
      ...prev,
      setupData: {
        ...prev.setupData,
        chickenType: id,
        breed: '',
        subtype: id === 'dual' ? prev.setupData.subtype : ''
      }
    }));
    setCustomBreed(false);
  };

  const popularBreeds = () => {
    const t = formData.setupData.chickenType;
    const st = formData.setupData.subtype;
    if (t === 'broiler') return chickenBreeds.broiler;
    if (t === 'layer') return chickenBreeds.layer;
    if (st === 'kienyeji_improved') return kienyejiImprovedBreeds;
    if (st === 'kienyeji_pure') return kienyejiPureBreeds;
    return [...kienyejiImprovedBreeds, ...kienyejiPureBreeds];
  };

  const validateForm = () => {
    const errors = [];
    if (!formData.name || formData.name.length < 2) errors.push('Farm name must be at least 2 characters');
    if (!isEdit && addFlockNow) {
      if (!formData.setupData.chickenType) errors.push('Select a flock type');
      if (!formData.setupData.chickenCount || formData.setupData.chickenCount < 1) errors.push('How many birds are in this flock?');
      if (formData.setupData.chickAge === '' || formData.setupData.chickAge < 0 || formData.setupData.chickAge > MAX_INITIAL_AGE_DAYS) {
        errors.push('Tell us how old the birds were on the arrival date');
      }
      if (!formData.setupData.setupDate) errors.push('Arrival date is required');
    }
    return errors;
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const validationErrors = validateForm();
      if (validationErrors.length > 0) {
        alert(validationErrors.join('\n'));
        setLoading(false);
        return;
      }

      const locationLabel = formatPlace(formData) || formData.location || undefined;
      const farmPayload = {
        name: formData.name,
        location: locationLabel,
        county: formData.county || undefined,
        subCounty: formData.subCounty || undefined,
        town: formData.town || undefined
      };
      if (formData.coordinates && typeof formData.coordinates === 'object') farmPayload.coordinates = formData.coordinates;
      if (formData.notes) farmPayload.notes = formData.notes;

      const result = isEdit ? await updateFarm(editId, farmPayload) : await createFarm(farmPayload);
      if (result.success) {
        try {
          const farmId = result.farm.id;
          const hasInitial = !isEdit && addFlockNow && Boolean(formData.setupData?.chickenCount && formData.setupData?.setupDate);
          if (hasInitial) {
            const initialName = `${formData.setupData.chickenType || 'flock'}-${apiDateToInput(formData.setupData.setupDate)}`;
            await farmAPI.createCohort(farmId, {
              name: initialName,
              type: formData.setupData.chickenType || 'broiler',
              breed: formData.setupData.breed || undefined,
              startQty: Number(formData.setupData.chickenCount || 0),
              startDate: dateInputToApi(formData.setupData.setupDate),
              initialAgeDays: parseAgeDays(formData.setupData.chickAge),
              housingType: formData.setupData.housingType || undefined,
              feedingSystem: formData.setupData.feedingSystem || undefined,
              managementSystem: formData.setupData.managementSystem || undefined,
              subtype: formData.setupData.subtype || undefined,
              purpose: (formData.setupData.chickenType === 'broiler' ? 'meat' : (formData.setupData.chickenType === 'layer' ? 'eggs' : 'dual')),
              targetMarketAgeDays: formData.setupData.targetMarketAgeDays === '' ? undefined : Number(formData.setupData.targetMarketAgeDays),
              status: 'active'
            });
          }
          for (const c of (isEdit || !addFlockNow ? [] : extraCohorts)) {
            if (!c?.name || !c?.type || !c?.startQty || !c?.startDate) continue;
            await farmAPI.createCohort(farmId, {
              name: c.name,
              type: c.type,
              breed: c.breed || '',
              startQty: Number(c.startQty),
              startDate: dateInputToApi(c.startDate),
              initialAgeDays: parseAgeDays(c.initialAgeDays),
              housingType: c.housingType || '',
              feedingSystem: c.feedingSystem || '',
              targetMarketAgeDays: c.targetMarketAgeDays === '' || c.targetMarketAgeDays == null ? undefined : Number(c.targetMarketAgeDays),
              status: 'active',
              notes: c.notes || ''
            });
          }
        } catch (e) {
          console.warn('Failed to create some flocks:', e?.message || e);
        }
        navigate(`/farm/${result.farm.id}`);
      }
    } catch (error) {
      alert(`Error: ${error.message || 'Unknown error'}`);
    } finally {
      setLoading(false);
    }
  };

  const isStepValid = () => {
    if (currentStep === 1) return formData.name.trim().length >= 2;
    return true;
  };

  const flockSummary = addFlockNow && formData.setupData.chickenCount
    ? `${formData.setupData.chickenCount} ${formData.setupData.chickenType}${formData.setupData.breed ? ` · ${formData.setupData.breed}` : ''}`
    : 'No flock yet — you can add one later';

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-0 pb-8">
      <div className="relative overflow-hidden bg-gradient-to-br from-green-600 via-emerald-600 to-teal-700 rounded-2xl p-5 sm:p-7 text-white mb-6">
        <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="relative flex items-center gap-3">
          <div className="p-2.5 bg-white/20 backdrop-blur rounded-xl">
            <BuildingOfficeIcon className="h-7 w-7" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold">{isEdit ? 'Edit farm' : 'Set up a farm'}</h1>
            <p className="text-green-100 text-sm">
              {isEdit ? 'Update the name and location. Flocks are managed from the farm page.' : 'Two minutes. Name the farm, then tell us about the birds you have now.'}
            </p>
          </div>
        </div>
      </div>

      {!isEdit && (
        <div className="mb-6">
          <ol className="flex items-center gap-2">
            {steps.map((step, idx) => {
              const done = step.id < currentStep;
              const active = step.id === currentStep;
              return (
                <li key={step.id} className="flex-1">
                  <button
                    type="button"
                    onClick={() => { if (step.id < currentStep) setCurrentStep(step.id); }}
                    className="w-full text-left"
                  >
                    <div className={`h-1.5 rounded-full mb-2 ${done || active ? 'bg-green-600' : 'bg-gray-200'}`} />
                    <div className="flex items-center gap-1.5">
                      <span className={`text-xs font-semibold ${active ? 'text-green-700' : done ? 'text-green-600' : 'text-gray-400'}`}>
                        {done ? '✓' : step.id}
                      </span>
                      <span className={`text-xs sm:text-sm font-medium ${active ? 'text-gray-900' : 'text-gray-500'}`}>
                        {step.name}
                      </span>
                    </div>
                    {idx === currentStep - 1 && (
                      <p className="hidden sm:block text-xs text-gray-500 mt-0.5">{step.description}</p>
                    )}
                  </button>
                </li>
              );
            })}
          </ol>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 sm:p-7">
        {(isEdit || currentStep === 1) && (
          <div className="space-y-5">
            <div>
              <label htmlFor="name" className="label">Farm name</label>
              <input
                id="name"
                name="name"
                required
                className="input text-base"
                placeholder="e.g. JUMWA Farm"
                value={formData.name}
                onChange={handleChange}
                autoFocus
              />
            </div>
            <div>
              <label className="label">Location <span className="text-gray-400 font-normal">(optional)</span></label>
              <p className="text-xs text-gray-500 mb-2">Used so nearby clients can find this farm by county.</p>
              <KenyaLocationFields
                county={formData.county}
                subCounty={formData.subCounty}
                town={formData.town}
                onChange={(patch) => setFormData((prev) => ({ ...prev, ...patch }))}
              />
            </div>
            {isEdit && (
              <div>
                <label htmlFor="notes" className="label">Notes</label>
                <textarea id="notes" name="notes" rows={3} className="input" value={formData.notes} onChange={handleChange} />
              </div>
            )}
          </div>
        )}

        {!isEdit && currentStep === 2 && (
          <div className="space-y-6">
            <label className="flex items-start gap-3 p-3 rounded-xl border border-gray-200 bg-gray-50 cursor-pointer">
              <input
                type="checkbox"
                className="mt-1 h-4 w-4 text-green-600 rounded"
                checked={addFlockNow}
                onChange={(e) => setAddFlockNow(e.target.checked)}
              />
              <span>
                <span className="block text-sm font-medium text-gray-900">I have birds to add now</span>
                <span className="block text-xs text-gray-500">Uncheck if this is just the farm record. You can add a flock later.</span>
              </span>
            </label>

            {addFlockNow && (
              <>
                <div>
                  <p className="label mb-2">What kind of flock is this?</p>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                    {TYPE_CARDS.map((t) => {
                      const selected = formData.setupData.chickenType === t.id;
                      return (
                        <button
                          key={t.id}
                          type="button"
                          onClick={() => setType(t.id)}
                          className={`text-left rounded-xl border p-3 transition ${
                            selected
                              ? 'border-green-600 bg-green-50 ring-1 ring-green-600'
                              : 'border-gray-200 hover:border-green-300 hover:bg-green-50/40'
                          }`}
                        >
                          <div className="text-sm font-semibold text-gray-900">{t.title}</div>
                          <div className="text-xs text-gray-500 mt-1">{t.hint}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {formData.setupData.chickenType === 'dual' && (
                  <div>
                    <label className="label">Kienyeji type</label>
                    <div className="grid grid-cols-2 gap-2">
                      {[
                        { id: 'kienyeji_improved', label: 'Improved' },
                        { id: 'kienyeji_pure', label: 'Pure / indigenous' }
                      ].map((s) => (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => setFormData((p) => ({ ...p, setupData: { ...p.setupData, subtype: s.id, breed: '' } }))}
                          className={`rounded-xl border px-3 py-2 text-sm ${
                            formData.setupData.subtype === s.id
                              ? 'border-green-600 bg-green-50 font-medium'
                              : 'border-gray-200'
                          }`}
                        >
                          {s.label}
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <label htmlFor="chickenCount" className="label">How many birds?</label>
                  <input
                    type="number"
                    id="chickenCount"
                    name="setupData.chickenCount"
                    min="1"
                    className="input"
                    placeholder="e.g. 200"
                    value={formData.setupData.chickenCount}
                    onChange={handleChange}
                  />
                </div>

                <FlockTimingFields
                  required
                  idPrefix="setup-"
                  type={formData.setupData.chickenType}
                  subtype={formData.setupData.subtype}
                  startDate={formData.setupData.setupDate}
                  initialAgeDays={formData.setupData.chickAge}
                  targetMarketAgeDays={formData.setupData.targetMarketAgeDays}
                  onChange={(patch) => {
                    setFormData((prev) => ({
                      ...prev,
                      setupData: {
                        ...prev.setupData,
                        ...(patch.startDate !== undefined ? { setupDate: patch.startDate } : {}),
                        ...(patch.initialAgeDays !== undefined ? { chickAge: patch.initialAgeDays } : {}),
                        ...(patch.targetMarketAgeDays !== undefined ? { targetMarketAgeDays: patch.targetMarketAgeDays } : {})
                      }
                    }));
                  }}
                />

                <button
                  type="button"
                  className="text-sm text-green-700 font-medium"
                  onClick={() => setShowMore((v) => !v)}
                >
                  {showMore ? 'Hide breed and housing' : 'Add breed, housing and feeding (optional)'}
                </button>

                {showMore && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-1">
                    <div className="sm:col-span-2">
                      <label className="label">Breed</label>
                      <select
                        className="input"
                        value={customBreed ? 'custom' : formData.setupData.breed}
                        onChange={(e) => {
                          if (e.target.value === 'custom') {
                            setCustomBreed(true);
                            setFormData((p) => ({ ...p, setupData: { ...p.setupData, breed: '' } }));
                          } else {
                            setCustomBreed(false);
                            handleChange({ target: { name: 'setupData.breed', value: e.target.value } });
                          }
                        }}
                      >
                        <option value="">Select if you know it</option>
                        {popularBreeds().map((b) => <option key={b} value={b}>{b}</option>)}
                        <option value="custom">Other…</option>
                      </select>
                      {customBreed && (
                        <input className="input mt-2" placeholder="Breed name" name="setupData.breed" value={formData.setupData.breed} onChange={handleChange} />
                      )}
                    </div>
                    <div>
                      <label className="label">Housing</label>
                      <select className="input" name="setupData.housingType" value={formData.setupData.housingType} onChange={handleChange}>
                        <option value="">Optional</option>
                        {housingOptions.map((h) => <option key={h.value} value={h.value}>{h.label}</option>)}
                      </select>
                    </div>
                    <div>
                      <label className="label">Management</label>
                      <select className="input" name="setupData.managementSystem" value={formData.setupData.managementSystem} onChange={handleChange}>
                        <option value="">Optional</option>
                        <option value="intensive">Intensive</option>
                        <option value="semi_intensive">Semi-intensive</option>
                        <option value="free_range">Free-range</option>
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="label">Feeding</label>
                      <select className="input" name="setupData.feedingSystem" value={formData.setupData.feedingSystem} onChange={handleChange}>
                        <option value="">Optional</option>
                        {feedingOptions.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label className="label">Notes</label>
                      <textarea name="notes" rows={2} className="input" value={formData.notes} onChange={handleChange} placeholder="Anything else about this farm" />
                    </div>
                  </div>
                )}

                <div className="border-t border-gray-100 pt-4">
                  <div className="flex items-center justify-between mb-3">
                    <p className="text-sm font-medium text-gray-900">Another flock on this farm?</p>
                    <button
                      type="button"
                      className="inline-flex items-center text-sm text-green-700 font-medium"
                      onClick={() => setExtraCohorts((prev) => ([...prev, {
                        name: '', type: 'broiler', breed: '', startQty: '', startDate: todayInputValue(),
                        initialAgeDays: 0, targetMarketAgeDays: '', housingType: '', feedingSystem: '', notes: ''
                      }]))}
                    >
                      <PlusIcon className="h-4 w-4 mr-1" /> Add flock
                    </button>
                  </div>
                  {extraCohorts.map((c, idx) => (
                    <div key={idx} className="border border-gray-200 rounded-xl p-3 mb-3 bg-gray-50">
                      <div className="flex justify-between items-center mb-2">
                        <span className="text-xs font-medium text-gray-500">Extra flock {idx + 1}</span>
                        <button type="button" className="text-gray-400 hover:text-red-600" onClick={() => setExtraCohorts((prev) => prev.filter((_, i) => i !== idx))}>
                          <XMarkIcon className="h-4 w-4" />
                        </button>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <input className="input text-sm" placeholder="Name" value={c.name} onChange={(e) => setExtraCohorts((p) => p.map((x, i) => i === idx ? { ...x, name: e.target.value } : x))} />
                        <select className="input text-sm" value={c.type} onChange={(e) => setExtraCohorts((p) => p.map((x, i) => i === idx ? { ...x, type: e.target.value } : x))}>
                          <option value="broiler">Broiler</option>
                          <option value="layer">Layer</option>
                          <option value="dual">Kienyeji</option>
                        </select>
                        <input type="number" className="input text-sm" placeholder="Birds" value={c.startQty} onChange={(e) => setExtraCohorts((p) => p.map((x, i) => i === idx ? { ...x, startQty: e.target.value } : x))} />
                        <select className="input text-sm" value={c.breed} onChange={(e) => setExtraCohorts((p) => p.map((x, i) => i === idx ? { ...x, breed: e.target.value } : x))}>
                          <option value="">Breed (optional)</option>
                          {(breedOptions[c.type] || []).map((b) => <option key={b} value={b}>{b}</option>)}
                        </select>
                        <div className="sm:col-span-2">
                          <FlockTimingFields
                            idPrefix={`extra-${idx}-`}
                            type={c.type}
                            subtype={c.subtype}
                            startDate={c.startDate}
                            initialAgeDays={c.initialAgeDays}
                            targetMarketAgeDays={c.targetMarketAgeDays}
                            onChange={(patch) => setExtraCohorts((p) => p.map((x, i) => i === idx ? { ...x, ...patch } : x))}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {!isEdit && currentStep === 3 && (
          <div className="space-y-5">
            <div className="rounded-xl border border-gray-200 divide-y divide-gray-100">
              <div className="p-4 flex justify-between">
                <div>
                  <p className="text-xs text-gray-500">Farm</p>
                  <p className="font-semibold text-gray-900">{formData.name}</p>
                  <p className="text-sm text-gray-500">{formatPlace(formData) || formData.location || 'Location not set'}</p>
                </div>
                <button type="button" className="text-xs text-green-700" onClick={() => setCurrentStep(1)}>Edit</button>
              </div>
              <div className="p-4 flex justify-between">
                <div>
                  <p className="text-xs text-gray-500">First flock</p>
                  <p className="font-semibold text-gray-900 capitalize">{flockSummary}</p>
                  {addFlockNow && formData.setupData.setupDate && formData.setupData.chickAge !== '' && (
                    <p className="text-sm text-gray-500">
                      Arrived {apiDateToInput(formData.setupData.setupDate)}, {formatAge(biologicalAgeDays(formData.setupData.setupDate, formData.setupData.chickAge))} old today
                    </p>
                  )}
                  {formData.setupData.housingType && (
                    <p className="text-sm text-gray-500">{pretty(formData.setupData.housingType, housingOptions)}</p>
                  )}
                </div>
                <button type="button" className="text-xs text-green-700" onClick={() => setCurrentStep(2)}>Edit</button>
              </div>
              {extraCohorts.length > 0 && addFlockNow && (
                <div className="p-4">
                  <p className="text-xs text-gray-500">{extraCohorts.length} extra flock{extraCohorts.length === 1 ? '' : 's'}</p>
                  <ul className="text-sm text-gray-800 mt-1 space-y-0.5">
                    {extraCohorts.map((c, i) => (
                      <li key={i}>{c.name || `Flock ${i + 1}`} · {c.startQty || '?'} {c.type}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
            {addFlockNow && (
              <div className="flex gap-2 rounded-xl bg-green-50 border border-green-100 p-3 text-sm text-green-800">
                <InformationCircleIcon className="h-5 w-5 flex-shrink-0" />
                <p>We will build a vaccination and feeding schedule from the birds’ age on the arrival date, not from today.</p>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="mt-5 flex justify-between gap-3">
        <button
          type="button"
          onClick={() => (isEdit || currentStep === 1 ? navigate(-1) : setCurrentStep(currentStep - 1))}
          className="btn-outline text-sm px-4 py-2"
        >
          {isEdit || currentStep === 1 ? 'Cancel' : 'Back'}
        </button>
        {isEdit || currentStep === steps.length ? (
          <button type="button" onClick={handleSubmit} disabled={loading || !isStepValid()} className="btn-primary text-sm px-5 py-2 disabled:opacity-50">
            {loading ? <><LoadingSpinner size="sm" className="mr-2" />Saving…</> : (isEdit ? 'Save farm' : 'Create farm')}
          </button>
        ) : (
          <button type="button" onClick={() => setCurrentStep(currentStep + 1)} disabled={!isStepValid()} className="btn-primary text-sm px-5 py-2 disabled:opacity-50">
            Continue
          </button>
        )}
      </div>
    </div>
  );
};

export default FarmSetup;
